/**
 * Constantes que definem os nomes dos breakpoints.
 */
export const BREAKPOINTS = {
  X_SMALL: 'x-small',
  SMALL: 'small',
  MEDIUM: 'medium',
  LARGE: 'large',
  X_LARGE: 'x-large',
} as const

/**
 * Tipo que representa os valores possíveis de breakpoint.
 */
export type Breakpoint = (typeof BREAKPOINTS)[keyof typeof BREAKPOINTS]

/**
 * Configurações específicas de um breakpoint.
 */
type BreakpointSettings = {
  name: Breakpoint
  query: string
  minWidth: number
  maxWidth?: number
}

/**
 * Configurações dos breakpoints, mapeando nome para suas definições.
 * É privada ao módulo.
 */
const BREAKPOINT_SETTINGS: Record<Breakpoint, BreakpointSettings> = {
  [BREAKPOINTS.X_SMALL]: {
    name: BREAKPOINTS.X_SMALL,
    query: '(max-width: 575px)',
    minWidth: 0,
    maxWidth: 575,
  },
  [BREAKPOINTS.SMALL]: {
    name: BREAKPOINTS.SMALL,
    query: '(min-width: 576px) and (max-width: 991px)',
    minWidth: 576,
    maxWidth: 991,
  },
  [BREAKPOINTS.MEDIUM]: {
    name: BREAKPOINTS.MEDIUM,
    query: '(min-width: 992px) and (max-width: 1279px)',
    minWidth: 992,
    maxWidth: 1279,
  },
  [BREAKPOINTS.LARGE]: {
    name: BREAKPOINTS.LARGE,
    query: '(min-width: 1280px) and (max-width: 1599px)',
    minWidth: 1280,
    maxWidth: 1599,
  },
  [BREAKPOINTS.X_LARGE]: {
    name: BREAKPOINTS.X_LARGE,
    query: '(min-width: 1600px)',
    minWidth: 1600,
  },
}

/**
 * Classe que observa mudanças nos breakpoints baseados em media queries.
 */
export class BreakpointObserver {
  private currentBreakpoint: Breakpoint | null = null
  private mediaQueries: { config: BreakpointSettings; mql: MediaQueryList }[] = []
  private listeners: Set<(breakpoint: Breakpoint) => void> = new Set()

  /**
   * Cria uma nova instância do observador e inicia a observação dos breakpoints.
   */
  constructor() {
    this.init()
  }

  /**
   * Inicializa os MediaQueryLists para cada breakpoint, adicionando listeners.
   */
  private init() {
    for (const breakpointName of Object.values(BREAKPOINTS)) {
      const config = BREAKPOINT_SETTINGS[breakpointName]
      const mql = window.matchMedia(config.query)
      this.mediaQueries.push({ config, mql })

      if (mql.matches) {
        this.currentBreakpoint = config.name
      }

      mql.addEventListener('change', this.handleChange)
    }
  }

  /**
   * Função chamada quando ocorre mudança em algum media query.
   * Atualiza o breakpoint atual e notifica os listeners se houver mudança.
   */
  private handleChange = () => {
    for (const { config, mql } of this.mediaQueries) {
      if (mql.matches && this.currentBreakpoint !== config.name) {
        this.currentBreakpoint = config.name
        this.notifyListeners()
        break
      }
    }
  }

  /**
   * Notifica todos os callbacks inscritos sobre a mudança do breakpoint atual.
   */
  private notifyListeners() {
    if (this.currentBreakpoint === null) return

    for (const listener of this.listeners) {
      listener(this.currentBreakpoint)
    }
  }

  /**
   * Retorna o breakpoint atual detectado.
   * @returns {Breakpoint | null} Breakpoint atual ou null se não definido
   */
  getCurrentBreakpoint(): Breakpoint | null {
    return this.currentBreakpoint
  }

  /**
   * Retorna as configurações completas de um breakpoint específico.
   * @param {Breakpoint} breakpoint - Breakpoint para consulta
   * @returns {BreakpointSettings | undefined} Configurações do breakpoint, ou undefined se não encontrado
   */
  getBreakpointSettings(breakpoint: Breakpoint): BreakpointSettings | undefined {
    return BREAKPOINT_SETTINGS[breakpoint]
  }

  /**
   * Retorna a query CSS associada a um breakpoint.
   * @param {Breakpoint} breakpoint - Breakpoint para consulta
   * @returns {string | undefined} Query CSS, ou undefined se não encontrado
   */
  getBreakpointQuery(breakpoint: Breakpoint): string | undefined {
    return BREAKPOINT_SETTINGS[breakpoint]?.query
  }

  /**
   * Retorna uma lista com todos os breakpoints disponíveis.
   * @returns {Breakpoint[]} Array com todos os breakpoints
   */
  getAllBreakpoints(): Breakpoint[] {
    return Object.values(BREAKPOINTS)
  }

  /**
   * Retorna uma lista de breakpoints filtrada segundo o tipo e breakpoint base.
   * Tipos aceitos:
   * - 'above': todos acima do breakpoint informado
   * - 'below': todos abaixo do breakpoint informado
   * - 'from': breakpoint informado e todos acima
   * - 'until': breakpoint informado e todos abaixo
   * - 'only': somente o breakpoint informado
   * @param {'above' | 'below' | 'from' | 'until' | 'only'} type - Tipo de filtro
   * @param {Breakpoint} breakpoint - Breakpoint base para filtro
   * @returns {Breakpoint[]} Lista filtrada de breakpoints
   */
  static getBreakpoints(type: 'above' | 'below' | 'from' | 'until' | 'only', breakpoint: Breakpoint): Breakpoint[] {
    const breakpointOrder = Object.values(BREAKPOINTS)
    const index = breakpointOrder.indexOf(breakpoint)

    if (index === -1) return []

    switch (type) {
      case 'above':
        return breakpointOrder.slice(index + 1)
      case 'below':
        return breakpointOrder.slice(0, index)
      case 'from':
        return breakpointOrder.slice(index)
      case 'until':
        return breakpointOrder.slice(0, index + 1)
      case 'only':
        return [breakpoint]
      default:
        return []
    }
  }

  /**
   * Adiciona um listener para receber notificações quando o breakpoint mudar.
   * O callback é chamado imediatamente com o breakpoint atual (se houver).
   * @param {(breakpoint: Breakpoint) => void} callback - Função para receber notificações
   */
  subscribe(callback: (breakpoint: Breakpoint) => void): void {
    this.listeners.add(callback)
    if (this.currentBreakpoint !== null) {
      callback(this.currentBreakpoint)
    }
  }

  /**
   * Remove um listener previamente registrado.
   * @param {(breakpoint: Breakpoint) => void} callback - Função a ser removida
   */
  unsubscribe(callback: (breakpoint: Breakpoint) => void): void {
    this.listeners.delete(callback)
  }

  /**
   * Remove todos os listeners e para a observação dos media queries.
   */
  destroy(): void {
    for (const { mql } of this.mediaQueries) {
      mql.removeEventListener('change', this.handleChange)
    }

    this.listeners.clear()
    this.mediaQueries = []
    this.currentBreakpoint = null
  }
}
